import React, { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { CardElement, useStripe, useElements as useStripeElements } from "@stripe/react-stripe-js"
import {
  CardNumberElement as MulpayCardNumberElement,
  CardCvcElement as MulpayCardCvcElement,
  CardExpiryElement as MulpayCardExpiryElement,
  useElements as useMulpayElements,
  useMultiPayment, 
} from "@mul-pay/mptoken-react-js"

import { connect } from "react-redux"
import { mapDispatchProps, mapStateToProps } from "../../store/store"

import API from "api"
import { useLanguage } from "@languages/index"

import Button from "../../components/button"
import TextInputField from "../../components/textInput"
import RadioButton from "../../components/radioButton"

const mulpayStyleObject = {
  style: {
    base: {
      fontWeight: "normal",
      fontSize: "15px",
      // color: "#32325D",
      fontFamily: '"NotoSansJP", "Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: "antialiased",
      "::placeholder": {
        color: "#AAB7C4",
      },
      padding: "12px",
    },
    invalid: {
      color: "#888",
    },
  },
};

const PROVIDERS = {
  INNOPAY: "INNOPAY",
  STRIPE: "STRIPE",
  GMO: "GMO",
}

const CardCreate = (props) => {
  const navigate = useNavigate()
  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.pages.cards }

  const CARD_TYPES = [
    { value: 1, name: languageTexts.create_types_1, isHidden: false },
    { value: 4, name: languageTexts.create_types_2, isHidden: false },
    { value: 3, name: languageTexts.create_types_3, isHidden: false },
    { value: 2, name: languageTexts.create_types_4, isHidden: false },
  ]

  const stripe = useStripe()
  const stripeElements = useStripeElements()

  const mulpay = useMultiPayment();
  const mulpayElements = useMulpayElements();

  const [paymentProvider, setPaymentProvider] = useState(props.brand.paymentGatewayProvider)
  const [type, setType] = useState(CARD_TYPES[0])
  const [isCorpCard, setIsCorpCard] = useState(false)
  // CARD_INPUTS
  const [cardNumber, setCardNumber] = useState("")
  const [expirationMonth, setExpirationMonth] = useState("")
  const [expirationYear, setExpirationYear] = useState("")
  const [cardHolderName, setCardHolderName] = useState("")
  const [password, setPassword] = useState("")
  const [registrationNumber, setRegistrationNumber] = useState("")
  const [email, setEmail] = useState("")
  const [cardName, setCardName] = useState("")
  // INPUT_MANAGES
  const mulpayInputsRef = useRef(Object.fromEntries(['cardNumber', 'cardExpiry', 'cardCvc'].map((key) => {
    return [key, { elementType: key,complete:false, empty:true, invalid:false, error: null }]
  })))
  const [errorMessage, setErrorMessage] = useState("")
  // const [mobile, setMobile] = useState("")
  const [isInputFilled, setIsInputFilled] = useState<any>(false)
  const [isMulpayInputFilled, setIsMulpayInputFilled] = useState(props.brand.paymentGatewayProvider !== PROVIDERS.GMO ? true : false)
  const isSubmitting = useRef(false)


  useEffect(() => {
    const checkInputs = () => {
      if (paymentProvider === PROVIDERS.STRIPE) {
        return type && cardHolderName
      }
      if (paymentProvider === PROVIDERS.GMO) {
        return type && isMulpayInputFilled && cardHolderName
      }
      return type && cardHolderName && registrationNumber && password && cardNumber && expirationMonth && expirationYear
    }

    setIsInputFilled(checkInputs())
  }, [
    type,
    cardHolderName,
    registrationNumber,
    password,
    cardNumber,
    expirationMonth,
    expirationYear,
    cardHolderName,
    isCorpCard,
    paymentProvider,
    isMulpayInputFilled
  ])


  const storeCard = async () => {
    try {
      if (!isInputFilled) {
        setErrorMessage(languageTexts.create_text_1)
        return
      }

      if (isSubmitting.current) {
        return
      }
      isSubmitting.current = true

      let commonParams: any = {
        brandToken: props.auth.token,
        type: type.value,
        cardHolderName,
        isCorpCard,
        // mobile: mobile.replace(/\D/g, ""),
        userId: props.auth.user_id,
        brandId: props.auth.brand_id,
        paymentProvider: paymentProvider,
        email: email,
      }

      if (paymentProvider === PROVIDERS.STRIPE) {
        // STRIPE payment
        const cardElement = stripeElements.getElement(CardElement)

        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            name: cardHolderName,
            email: email,
          },
        })

        if (error) {
          isSubmitting.current = false
          setErrorMessage(error.message)
          return
        }

        const { card } = paymentMethod
        const exp_month = card.exp_month.toString().padStart(2, "0")
        const exp_year = card.exp_year.toString().slice(-2)
        const expirationDate = `${exp_month}${exp_year}`

        commonParams = {
          ...commonParams,
          name: card.brand,
          paymentMethod: paymentMethod.id,
          expirationDate: expirationDate.replace(/\D/g, ""),
          cardNumber: `************${card.last4}`,
        }

      } else if (paymentProvider === PROVIDERS.GMO) {
        if (!mulpay || !mulpayElements) {
          isSubmitting.current = false
          setErrorMessage("Mulpay or MulpayElements not initialized");
          return;
        }

        const cardElement = mulpayElements.getElement("cardNumber");
        if (cardElement === null) {
          isSubmitting.current = false
          setErrorMessage("Mulpay Card element not found");
          return;
        }

        const mulpayErrorMessage = getMulpayInputErrorText()
        if (mulpayErrorMessage != null) {
          isSubmitting.current = false
          setErrorMessage(mulpayErrorMessage)
          return
        }

        const tokenResponse:any = await mulpay.getTokenThroughIframe(
          cardElement, 
          cardHolderName, 
          { tokenNumber: "1" }
        )
        const token = tokenResponse?.tokenList?.[0]
        const maskedCardNumber = tokenResponse?.maskedCardNumber
        if (tokenResponse?.result !== 'success' || token == null) {
          isSubmitting.current = false
          setErrorMessage(JSON.stringify(tokenResponse));
          return
        }

        commonParams = {
          ...commonParams,
          token,
          cardNumber: maskedCardNumber
        }

      } else {
        // INNOPAY payment
        const expirationDate = `${expirationMonth}${expirationYear}`
        commonParams = {
          ...commonParams,
          password: password.replace(/\D/g, ""),
          expirationDate: expirationDate.replace(/\D/g, ""),
          registrationNumber: registrationNumber.replace(/\D/g, ""),
          cardNumber: cardNumber.replace(/\D/g, ""),
        }
      }

      const response = await API.Card.store(commonParams)
      props.showModal({
        text: response.message,
        confirm: () => {
          if (props.onCardCreated) props.onCardCreated()
        },
      })
    } catch (error) {
      isSubmitting.current = false
      setErrorMessage(error.message)
    }
  }


  const getMulpayInputErrorText = () => {
    if (paymentProvider !== PROVIDERS.GMO)
      return null

    const inputs = mulpayInputsRef.current
    if (!inputs)
      return null

    const ELEMENT_TYPE__ERROR_MESSAGE__MAP = {
      'cardNumber': languageTexts.create_text_13,
      'cardExpiry': languageTexts.create_text_14,
      'cardCvc': languageTexts.create_text_15,
    }
    let isCompleted = true
    for (const elementType of Object.keys(inputs)) {
      if (inputs[elementType].invalid) {
        return ELEMENT_TYPE__ERROR_MESSAGE__MAP[elementType]
      }
      if (!inputs[elementType].complete) {
        isCompleted = false
      }
    }
    if (!isCompleted) {
      return languageTexts.create_text_1
    }

    return null
  }

  const handleMulpayEvent = (name:any) => (event:any) => {
    let newErrorMessage;
    if (name == 'change') {
      mulpayInputsRef.current = {
        ...mulpayInputsRef.current,
        [event.elementType]: event,
      }
    }

    switch (name) {
      case 'change':
      case 'blur':
        newErrorMessage = getMulpayInputErrorText()
        setErrorMessage(newErrorMessage)
        setIsMulpayInputFilled(newErrorMessage == null)
        break;
      case 'ready':
      case 'focus':
      default:
        break;
    }
  };
  
  useEffect(() => {
    if (isCorpCard) {
      setType(undefined)
    } else {
      setType(CARD_TYPES[0])
    }
  }, [isCorpCard])

  const renderTitle = () =>
    props.modal.title ? <span className="prose prose-xl text-black">{props.modal.title}</span> : null

  const renderText = () =>
    props.modal.text ? <span className="prose prose-md text-black mt-0 lg:mt-4">{props.modal.text}</span> : null

  return (
    <div
      className={`max-w-[800px] ${props.cardsContainerClassName ? props.cardsContainerClassName : "py-6 pb-10"}`}
      style={{ backgroundColor: "white" }}
    >
      {/* Prevent Autocomplete */}
      <input type="text" style={{ width: "0px", height: "0px", position: "absolute" }} />
      <input type="password" style={{ width: "0px", height: "0px", position: "absolute" }} />

      <div className="content flex flex-col">
        {renderTitle()}
        {renderText()}
      </div>

      <div className="inputs flex flex-col gap-3">
        {paymentProvider == PROVIDERS.INNOPAY ? (
          <>
            <div className="flex flex-row">
              <RadioButton
                tabClassName="px-[10px] py-3"
                selectedClassName="px-[10px] py-3 bg-black font-bold"
                selectedTextColor="WHITE"
                options={CARD_TYPES}
                selected={type}
                onSelect={(option) => setType(option)}
              />
            </div>

            <div className="flex flex-col">
              <span className="text-sm text-[#999999]">{languageTexts.create_text_2}</span>
              <span className="text-sm text-[#999999]">{languageTexts.create_text_3}</span>
            </div>
          </>
        ) : null}
        
        <div className="flex flex-row">
          {paymentProvider === PROVIDERS.STRIPE && (
            <div className="w-full border border-[#E8E8E8] py-[15px] px-[20px]">
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: "0.938rem",
                    },
                  },
                }}
              />
            </div>
          )}
          {paymentProvider === PROVIDERS.INNOPAY && (
            <div className="w-full">
              <TextInputField
                bordered
                inputClassName="py-[8px] px-[10px] text-[15px]"
                placeholder={languageTexts.create_text_5}
                onChangeText={setCardNumber}
                numbersOnly
              />
            </div>
          )}
          {(paymentProvider === PROVIDERS.GMO) && (
            <div className="w-full">
              <div className="border">
                <MulpayCardNumberElement
                  id="cardNumber"
                  onBlur={handleMulpayEvent("blur")}
                  onChange={handleMulpayEvent("change")}
                  onFocus={handleMulpayEvent("focus")}
                  options={{
                    ...mulpayStyleObject,
                    placeholder: '1234 1234 1234 1234'
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {paymentProvider === PROVIDERS.INNOPAY && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid grid-cols-2 gap-3">
                <TextInputField
                  bordered
                  inputClassName="py-[8px] px-[10px] text-[15px]"
                  placeholder="MM"
                  maxLength={2}
                  onChangeText={(value) => setExpirationMonth(value.replace(/\D/g, ""))}
                  numbersOnly
                />
                <TextInputField
                  bordered
                  inputClassName="py-[8px] px-[10px] text-[15px]"
                  placeholder="YY"
                  maxLength={2}
                  onChangeText={(value) => setExpirationYear(value.replace(/\D/g, ""))}
                  numbersOnly
                />
              </div>
              <div>
                <TextInputField
                  type="password"
                  bordered
                  inputClassName="py-[8px] px-[10px] text-[15px]"
                  placeholder={languageTexts.create_text_7}
                  onChangeText={setPassword}
                  maxLength={2}
                  numbersOnly
                />
              </div>
            </div>
          </>
        )}

        {(paymentProvider === PROVIDERS.GMO) && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="border">
                <MulpayCardExpiryElement
                  id="expiry"
                  onBlur={handleMulpayEvent("blur")}
                  onChange={handleMulpayEvent("change")}
                  onFocus={handleMulpayEvent("focus")}
                  onReady={handleMulpayEvent("ready")}
                  options={mulpayStyleObject}
                />
              </div>
              <div className="border">
                <MulpayCardCvcElement
                  id="cvc"
                  onBlur={handleMulpayEvent("blur")}
                  onChange={handleMulpayEvent("change")}
                  onFocus={handleMulpayEvent("focus")}
                  onReady={handleMulpayEvent("ready")}
                  options={mulpayStyleObject}
                />
              </div>
            </div>
          </>
        )}

        <div className="flex flex-row">
          <TextInputField
            bordered
            inputClassName="py-[8px] px-[10px] text-[15px]"
            placeholder={languageTexts.create_text_10}
            onChangeText={setCardHolderName}
          />
        </div>

        {paymentProvider === PROVIDERS.INNOPAY && (
          <div className="flex flex-row">
            <TextInputField
              bordered
              inputClassName="py-[8px] px-[10px] text-[15px]"
              placeholder={type && type.value === 2 ? languageTexts.create_text_8 : languageTexts.create_text_9}
              maxLength={type && type.value === 2 ? 10 : 6}
              numbersOnly
              onChangeText={setRegistrationNumber}
            />
          </div>
        )}

        {/* <div className="flex flex-row">
          <TextInputField bordered placeholder={languageTexts.create_text_4} onChangeText={setMobile} numbersOnly />
        </div> */}

        {paymentProvider !== PROVIDERS.GMO && (
          <div className="flex flex-row">
            <TextInputField
              bordered
              inputClassName="py-[8px] px-[10px] text-[15px]"
              placeholder={languageTexts.create_text_12}
              defaultValue={email}
              onChangeText={setEmail}
            />
          </div>
        )}

        {errorMessage && (
          <div className="flex flex-row">
            <span className="prose text-red-500">{errorMessage}</span>
          </div>
        )}
      </div>

      <div className=" flex flex-col gap-3 form-submit-button z-[1] mt-8">
        <Button
          textColor={"WHITE"}
          buttonColor={isInputFilled ? "BLACK" : "#b2b2b2"}
          borderColor={isInputFilled ? "BLACK" : "#b2b2b2"}
          text={languageTexts.create_text_11}
          onClick={storeCard}
        />

        <Button
          textColor={"BLACK"}
          buttonColor={"WHITE"}
          text={languageTexts.cancel2}
          onClick={() => {
            props.onClose ? props.onClose() : null
          }}
        />
      </div>
    </div>
  )
}

export default connect(mapStateToProps, mapDispatchProps)(CardCreate)
